import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { getMinutesFromTimeString } from '@/utils/helpers'
import { StaffType } from '@/utils/onboarding/type'
import { DoctorData } from '../ui/DoctorSelectionTable'

dayjs.extend(duration)
dayjs.extend(isSameOrAfter)

const specialityOptionSchema = z.object({
  _id: z.string().optional(),
  code: z.string().min(1),
  name: z.string().min(1),
  selectByDefault: z.boolean().optional(),
  isNew: z.boolean().optional(),
})

export default function useWaitingRoomFormSchema({ doctors, isAdmin }: { doctors?: DoctorData[]; isAdmin: boolean }) {
  const { t } = useTranslation()

  return useMemo(
    () =>
      z
        .object({
          avgWaitingTime: z.number(),
          allowPatientSelection: z.boolean(),
          description: z.string().optional(),
          doctors: z.array(z.string()),
          establishments: z.array(z.string()),
          isPrioritized: z.boolean().optional(),
          name: z.string().min(1),
          openingHours: z.array(
            z.object({
              day: z.number(),
              periods: z.array(
                z
                  .object({
                    open: z.string().min(1).regex(/^\d\d:\d\d$/, t('error.time.invalidFormat.message')),
                    close: z.string().min(1).regex(/^\d\d:\d\d$/, t('error.time.invalidFormat.message')),
                  })
                  .refine(
                    (data) => dayjs(data.close, 'HH:mm').isSameOrAfter(dayjs(data.open, 'HH:mm')),
                    { message: t('error.time.endTimeSoonerThanStartTime.message'), path: ['close'] }
                  )
              ),
            })
          ),
          price: z.number().min(0).optional(),
          paymentReceiverId: z.string().optional(),
          paymentDialogConfig: z.object({
            enabled: z.boolean(),
            showForRoles: z.array(z.enum([StaffType.MEDECIN, StaffType.PDS])),
          }),
          fairDistribution: z.object({
            // plain z.number() (no coerce): NumberInputController already stores numbers, and
            // z.coerce's unknown input type breaks the zodResolver/useForm typing under zod 4
            enabled: z.boolean(),
            maxConcurrent: z.number().int().min(1),
            cooldownSeconds: z.number().int().min(0),
            strategy: z.enum(['cap', 'offer']),
            offerTtlSeconds: z.number().int().min(5),
            prominentOffer: z.boolean(),
          }),
          readinessConfig: z.object({
            enabled: z.boolean(),
            prioritizeReady: z.boolean(),
            // '' = no checklist (mapped to undefined on submit)
            checklistTemplateId: z.string().optional(),
          }),
          nurseDoctorExclusions: z.object({
            enabled: z.boolean(),
            rules: z.array(z.object({ pdsId: z.string(), doctorIds: z.array(z.string()) })),
          })
            // Disabled configs submit no rules, so hidden draft errors cannot block saving.
            .transform((config) => config.enabled ? config : { enabled: false, rules: [] })
            .pipe(z.object({
              enabled: z.boolean(),
              rules: z.array(z.object({
                pdsId: z.string().min(1, t('field.exclusionNurse.required')),
                doctorIds: z.array(z.string()).min(1, t('field.exclusionDoctors.required')),
              })),
            })),
          coPresenceConfig: z.object({
            enabled: z.boolean(),
            allowAssign: z.boolean(),
          }),
          specialities: z.array(specialityOptionSchema),
        })
        .superRefine((data, ctx) => {
          const nurses = new Set<string>()
          data.nurseDoctorExclusions.rules.forEach((rule, index) => {
            if (nurses.has(rule.pdsId)) ctx.addIssue({ code: 'custom', message: t('field.exclusionNurse.duplicate'), path: ['nurseDoctorExclusions', 'rules', index, 'pdsId'] })
            nurses.add(rule.pdsId)
            // Keep rules intact when room membership changes; the editor must resolve stale doctors.
            if (rule.doctorIds.some((id) => !data.doctors.includes(id))) ctx.addIssue({ code: 'custom', message: t('field.exclusionDoctors.notInRoom'), path: ['nurseDoctorExclusions', 'rules', index, 'doctorIds'] })
          })
          // Validate paymentReceiverId is required when price > 0
          if (data.price && data.price > 0 && (!data.paymentReceiverId || data.paymentReceiverId === '')) {
            ctx.addIssue({
              code: 'custom',
              message: t('error.paymentReceiverId.required.message'),
              path: ['paymentReceiverId'],
            })
          }

          // Validate paymentReceiverId refers to a doctor with stripe activated
          if (data.paymentReceiverId && data.paymentReceiverId !== '' && isAdmin) {
            const doctor = _.find(doctors, (d) => d.id === data.paymentReceiverId)
            if (!doctor || !doctor.stripeActivated) {
              ctx.addIssue({
                code: 'custom',
                message: t('error.paymentReceiverId.notConfigured.message'),
                path: ['paymentReceiverId'],
              })
            }
          }

          // Validate overlapping periods
          for (let dayIdx = 0; dayIdx < data.openingHours.length; dayIdx++) {
            const dayItem = data.openingHours[dayIdx]
            for (let periodIdx = 0; periodIdx < dayItem.periods.length; periodIdx++) {
              const currentPeriod = dayItem.periods[periodIdx]
              const otherPeriods = _.differenceWith(dayItem.periods, [currentPeriod], _.isEqual)
              for (const otherPeriod of otherPeriods) {
                if (
                  currentPeriod.open?.match(/^\d\d:\d\d$/) &&
                  currentPeriod.close?.match(/^\d\d:\d\d$/) &&
                  otherPeriod.open?.match(/^\d\d:\d\d$/) &&
                  otherPeriod.close?.match(/^\d\d:\d\d$/) &&
                  dayjs.duration(getMinutesFromTimeString(currentPeriod.open), 'minutes').asMinutes() <
                    dayjs.duration(getMinutesFromTimeString(otherPeriod.close), 'minutes').asMinutes() &&
                  dayjs.duration(getMinutesFromTimeString(currentPeriod.close), 'minutes').asMinutes() >
                    dayjs.duration(getMinutesFromTimeString(otherPeriod.open), 'minutes').asMinutes()
                ) {
                  ctx.addIssue({
                    code: 'custom',
                    message: t('error.time.overlap.message'),
                    path: ['openingHours', dayIdx, 'periods', periodIdx, 'open'],
                  })
                  ctx.addIssue({
                    code: 'custom',
                    message: t('error.time.overlap.message'),
                    path: ['openingHours', dayIdx, 'periods', periodIdx, 'close'],
                  })
                  break
                }
              }
            }
          }
        }),
    [doctors, isAdmin, t]
  )
}

export type WaitingRoomFormData = z.infer<ReturnType<typeof useWaitingRoomFormSchema>>

export type UseWaitingRoomFormProps = {
  doctors?: DoctorData[]
  isAdmin: boolean
  initialValues: WaitingRoomFormData
  editable?: boolean
}

export function useWaitingRoomForm({ doctors, isAdmin, initialValues, editable }: UseWaitingRoomFormProps) {
  const schema = useWaitingRoomFormSchema({ doctors, isAdmin })

  const rhForm = useForm<WaitingRoomFormData>({ defaultValues: initialValues, resolver: zodResolver(schema) })

  useEffect(() => {
    if (!editable) rhForm.reset()
  }, [editable, rhForm.reset])

  useEffect(() => {
    rhForm.reset(initialValues)
  }, [initialValues, rhForm.reset])

  return { rhForm }
}
